// Enhanced Navigation Component
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Collapse,
  Chip,
  Badge,
  Tooltip,
  Box,
} from '@mui/material';
import {
  People,
  CalendarMonth,
  Spa,
  Inventory,
  Receipt,
  TrendingUp,
  AttachMoney,
  Security,
  ContentCut,
  ExpandLess,
  ExpandMore,
  AdminPanelSettings,
  SupervisorAccount,
  ManageAccounts,
  Analytics,
  Notifications,
  Settings,
  Assessment,
  CardMembership,
} from '@mui/icons-material';
import authService from '../../services/auth';
import { useCurrency } from '../../contexts/CurrencyContext';
import './Navigation.css';

const Navigation = ({ userPermissions, onMenuClick, isMobile }) => {
  const location = useLocation();
  const [adminMenuOpen, setAdminMenuOpen] = useState(true);
  const [reportsMenuOpen, setReportsMenuOpen] = useState(true);
  const user = authService.getUser();
  const { currencySymbol } = useCurrency();

  // Custom Currency Icon Component
  const CurrencyIcon = () => (
    <div className="currency-icon">
      {currencySymbol}
    </div>
  );

  //console.log('Navigation - Props received:', { userPermissions, user });

  // Check if user has permission to access a module
  const hasPermission = (module, action = 'view') => {
    // If user is admin or super admin, grant all permissions
    if (isAdmin() || isSuperAdmin()) return true;
    console.log('Navigation - userPermissions:', userPermissions);
    if (!userPermissions) {
      console.log(`Navigation - No userPermissions for ${module}.${action}`);
      return false;
    }

    const hasPerm = userPermissions[module]?.[action] === true;
    //console.log(`Navigation - hasPermission(${ module }.${ action }): ${ hasPerm }, userPermissions[${ module }]: `, userPermissions[module]);
    return hasPerm;
  };

  // Check if user has access to a feature
  const hasFeatureAccess = (feature) => {
    if (!feature) return true; // No feature requirement means always available

    const user = authService.getCompleteUser();
    if (!user || !user.features || !user.features.available_features) {
      return false;
    }

    // Check if user has the specific feature
    // Backend `available_features` often already contains only included features and may not
    // include an `is_included` flag. Support both shapes:
    // - { name: 'Reports & Analytics' } (included-by-definition)
    // - { name: 'Reports & Analytics', is_included: true }
    return user.features.available_features.some((f) => {
      if (f?.name !== feature) return false;
      return f?.is_included === undefined ? true : Boolean(f.is_included);
    });
  };

  // Filter menu items based on feature access
  const getFilteredMenuItems = () => {
    console.log("getFilteredMenuItems", mainMenuItems);
    return mainMenuItems.filter(item => {
      // Check both permission and feature access
      const hasPerm = hasPermission(item.module, 'view');
      const hasFeature = hasFeatureAccess(item.feature);//this menu is used for sub users. They have their own permissions which is handled by hasPerm. If a features is not available in a plan then sub user can't get that permission.

      return hasPerm;
    });
  };

  // Check if user is admin
  const isAdmin = () => {
    const isAdminUser = user?.role_name?.toLowerCase() === 'admin';
    //console.log(`Navigation - isAdmin check: role_name = "${user?.role_name}", isAdmin = ${ isAdminUser } `);
    return isAdminUser;
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    const isSuperAdminUser = user?.role_name?.toLowerCase() === 'super admin';
    //console.log(`Navigation - isSuperAdmin check: role_name = "${user?.role_name}", isSuperAdmin = ${ isSuperAdminUser } `);
    return isSuperAdminUser;
  };

  // Main menu items with feature requirements
  const mainMenuItems = [
    {
      text: 'Dashboard',
      icon: <Analytics />,
      path: '/dashboard',
      module: 'dashboard',
      feature: null, // Always available
      badge: null
    },
    {
      text: 'Invoices',
      icon: <Receipt />,
      path: '/dashboard/invoices',
      module: 'invoices',
      feature: 'Invoice Management',
      badge: null
    },
    {
      text: 'Clients',
      icon: <People />,
      path: '/dashboard/clients',
      module: 'clients',
      feature: 'Client Management',
      badge: null
    },
    {
      text: 'Follow-ups',
      icon: <Notifications />,
      path: '/dashboard/follow-ups',
      module: 'clients',
      feature: 'Client Management',
      badge: null
    },
    {
      text: 'Appointments',
      icon: <CalendarMonth />,
      path: '/dashboard/appointments',
      module: 'appointments',
      feature: 'Appointment Booking',
      badge: null
    },
    {
      text: 'Services',
      icon: <Spa />,
      path: '/dashboard/services',
      module: 'services',
      feature: 'Core', // Category access
      badge: null
    },
    {
      text: 'Products',
      icon: <Inventory />,
      path: '/dashboard/products',
      module: 'products',
      feature: 'Core', // Category access
      badge: null
    },
    {
      text: 'Memberships',
      icon: <CardMembership />,
      path: '/dashboard/memberships',
      module: 'memberships',
      feature: 'Core', // Category access
      badge: null
    },
    {
      text: 'Client Memberships',
      icon: <CardMembership />,
      path: '/dashboard/memberships/clients',
      module: 'clients',
      feature: 'Client Management',
      badge: null
    },
    {
      text: 'Brands',
      icon: <ContentCut />,
      path: '/dashboard/brands',
      module: 'brands',
      feature: 'Core', // Category access
      badge: null
    },
    {
      text: 'Expenses',
      icon: <CurrencyIcon />,
      path: '/dashboard/expenses',
      module: 'expenses',
      feature: 'Core', // Category access
      badge: null
    },
  ];

  // Admin menu items
  const adminMenuItems = [
    {
      text: 'Staff',
      icon: <SupervisorAccount />,
      path: '/dashboard/users',
      module: 'users',
      description: 'Manage system users'
    },
    {
      text: 'Roles',
      icon: <ManageAccounts />,
      path: '/dashboard/roles',
      module: 'roles',
      description: 'Configure user roles and permissions'
    },
    {
      text: 'Settings',
      icon: <Settings />,
      path: '/dashboard/settings',
      module: 'settings',
      description: 'Manage business settings and GST information'
    },
    {
      text: 'Subscriptions',
      icon: <Receipt />,
      path: '/dashboard/subscriptions',
      module: 'subscriptions',
      description: 'View subscription details and plan information'
    },
  ];

  // Reports menu items
  const reportsMenuItems = [
    {
      text: 'Sales',
      icon: <Assessment />,
      path: '/dashboard/reports/sales',
      module: 'reports',
      feature: 'Reports & Analytics',
      description: 'View sales reports and analytics'
    },
    {
      text: 'Staff',
      icon: <People />,
      path: '/dashboard/reports/staff',
      module: 'reports',
      feature: 'Reports & Analytics',
      description: 'View sales performance by staff member'
    },
    {
      text: 'Service & Products',
      icon: <Spa />,
      path: '/dashboard/reports/service-products',
      module: 'reports',
      feature: 'Reports & Analytics',
      description: 'View sales performance by services and products'
    },
  ];

  // Check if current path is active
  const isActivePath = (path) => {
    return location.pathname === path;
  };

  // Render menu item
  const renderMenuItem = (item, isAdminSection = false) => {
    // Admin and Super Admin users can access all items, others need permission check
    if (!isAdmin() && !isSuperAdmin() && !hasPermission(item.module)) {
      return null;
    }

    const active = isActivePath(item.path);

    return (
      <Tooltip
        key={item.path}
        title={item.description || item.text}
        placement="right"
        arrow
        classes={{
          tooltip: 'navigation-tooltip'
        }}
      >
        <ListItem disablePadding className="navigation-item">
          <ListItemButton
            onClick={() => onMenuClick(item.path)}
            className={`navigation-item-button ${active ? 'active' : ''}`}
          >
            <ListItemIcon
              className={`navigation-item-icon ${active ? 'active' : ''}`}
            >
              {item.badge ? (
                <Badge
                  badgeContent={item.badge.count}
                  color={item.badge.color}
                  overlap="circular"
                  className="navigation-badge"
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              className={`navigation-item-text ${active ? 'active' : ''}`}
            />
            {isAdminSection && item.badge && (
              <Chip
                label={item.badge.count}
                size="small"
                color={item.badge.color}
                className={`navigation-chip ${active ? 'active' : ''}`}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  };

  // Filter main menu items based on permissions and feature access
  const visibleMainItems = (isAdmin() || isSuperAdmin())
    ? mainMenuItems  // Show all items for admin/super admin
    : getFilteredMenuItems(); // Use feature-based filtering

  // Filter admin menu items
  // Administration section should be visible to Admin/Super Admin only.
  const visibleAdminItems = (isAdmin() || isSuperAdmin()) ? adminMenuItems : [];

  // Filter reports menu items
  const visibleReportsItems = (isAdmin() || isSuperAdmin())
    ? reportsMenuItems  // Show all reports items for admin/super admin
    : reportsMenuItems.filter(item => {
        const hasPerm = hasPermission(item.module, 'view');
        const hasFeature = hasFeatureAccess(item.feature);
        return hasPerm && hasFeature;
      });



  return (
    <div className="navigation-container">
      {/* Main Navigation */}
      <div className="navigation-section">
        <Typography
          variant="overline"
          className="navigation-section-title"
        >
          Main Menu
        </Typography>

        <List className="navigation-list">
          {visibleMainItems.map(item => renderMenuItem(item))}
        </List>
      </div>

      {/* Reports Section */}
      {visibleReportsItems.length > 0 && (
        <>
          <Divider className="navigation-divider" />
          <div className="admin-section">
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setReportsMenuOpen(!reportsMenuOpen)}
                className="admin-toggle-button"
              >
                <ListItemIcon className="admin-toggle-icon" sx={{ color: 'white' }}>
                  <Assessment />
                </ListItemIcon>
                <ListItemText
                  primary="Reports"
                  className="admin-toggle-text"
                  primaryTypographyProps={{
                    sx: { color: 'white !important' }
                  }}
                />
                <Box sx={{ color: 'white' }}>
                  {reportsMenuOpen ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </ListItemButton>
            </ListItem>

            <Collapse in={reportsMenuOpen} timeout="auto" unmountOnExit>
              <List className="navigation-list" sx={{ pl: 3 }}>
                {visibleReportsItems.map(item => renderMenuItem(item, true))}
              </List>
            </Collapse>
          </div>
        </>
      )}

      {/* Admin Section */}
      {(isAdmin() || isSuperAdmin()) && visibleAdminItems.length > 0 && (
        <>
          <Divider className="navigation-divider" />
          <div className="admin-section">
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                className="admin-toggle-button"
              >
                <ListItemIcon className="admin-toggle-icon" sx={{ color: 'white' }}>
                  <Security />
                </ListItemIcon>
                <ListItemText
                  primary="Administration"
                  className="admin-toggle-text"
                  primaryTypographyProps={{
                    sx: { color: 'white !important' }
                  }}
                />
                <Box sx={{ color: 'white' }}>
                  {adminMenuOpen ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </ListItemButton>
            </ListItem>

            <Collapse in={adminMenuOpen} timeout="auto" unmountOnExit>
              <List className="navigation-list" sx={{ pl: 3 }}>
                {visibleAdminItems.map(item => renderMenuItem(item, true))}
              </List>
            </Collapse>
          </div>
        </>
      )}


    </div>
  );
};

export default Navigation;
